<template>
  <div dir="rtl" class="min-h-screen bg-base text-white font-sans pb-24">
    <div class="max-w-7xl mx-auto px-4 pt-6">
      
      <!-- Header -->
      <header class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 class="text-2xl font-extrabold text-white flex items-center gap-2">
            <span class="p-2 rounded-xl bg-[#b9f1cf]/10 text-[#b9f1cf] shadow-[0_0_15px_rgba(185,241,207,0.15)]">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            تاریخچه واریزها
          </h1>
          <p class="text-white/50 text-sm mt-1">لیست تمام واریزی‌های انجام شده به کیف پول</p>
        </div>

        <div class="flex items-center gap-2 bg-[#141a1d] p-1 rounded-xl ring-1 ring-white/10">
          <div class="px-3 py-1.5 rounded-lg bg-white/5 text-xs text-white/70">
            تعداد کل: <span class="font-bold text-white">{{ deposits.length }}</span>
          </div>
          <button 
            @click="fetchDeposits" 
            class="p-1.5 rounded-lg hover:bg-white/10 text-white/50 hover:text-[#b9f1cf] transition"
            title="بروزرسانی"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </header>

      <!-- Loading State -->
      <div v-if="loading" class="rounded-2xl bg-[#141a1d] ring-1 ring-white/5 overflow-hidden">
         <div class="h-12 bg-white/5 border-b border-white/5"></div>
         <div v-for="i in 5" :key="i" class="h-16 border-b border-white/5 animate-pulse"></div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="deposits.length === 0" class="flex flex-col items-center justify-center py-20 bg-[#141a1d]/50 rounded-3xl border border-dashed border-white/10">
          <div class="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-4 text-white/20">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p class="text-white/60 font-medium">هیچ سابقه واریزی یافت نشد.</p>
        </div>

        <div v-else>
          <!-- DESKTOP TABLE -->
          <div class="hidden md:block rounded-2xl bg-[#141a1d]/90 backdrop-blur-xl ring-1 ring-[#334D44]/40 shadow-xl overflow-hidden">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-white/5">
                <thead>
                  <tr class="bg-white/5">
                    <th scope="col" class="px-6 py-4 text-right text-xs font-bold text-[#b9f1cf] uppercase tracking-wider">شناسه پیگیری</th>
                    <th scope="col" class="px-6 py-4 text-center text-xs font-bold text-white/50 uppercase tracking-wider">مبلغ (تومان)</th>
                    <th scope="col" class="px-6 py-4 text-center text-xs font-bold text-white/50 uppercase tracking-wider">تاریخ و زمان</th>
                    <th scope="col" class="px-6 py-4 text-center text-xs font-bold text-white/50 uppercase tracking-wider">وضعیت</th>
                    <th scope="col" class="px-6 py-4 text-center text-xs font-bold text-white/50 uppercase tracking-wider">رسید واریز</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/5">
                  <tr 
                    v-for="item in deposits" 
                    :key="item._id" 
                    class="group transition-colors hover:bg-white/[0.02]"
                  >
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center gap-3">
                        <div class="h-8 w-8 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-400 group-hover:scale-110 transition-transform">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12" />
                          </svg>
                        </div>
                        <div class="font-mono text-sm text-white/80 group-hover:text-white transition">{{ item.uid }}</div>
                      </div>
                    </td>
                    
                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <div class="text-lg font-bold font-mono text-white tracking-tight">
                        {{ formatMoney(item.amount) }}
                      </div>
                    </td>

                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <div class="flex flex-col items-center">
                        <span class="text-sm font-medium text-white/90">{{ formatDate(item.createdAt).date }}</span>
                        <span class="text-[10px] text-white/40 font-mono">{{ formatDate(item.createdAt).time }}</span>
                      </div>
                    </td>

                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <span 
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold border"
                        :class="getStatusClass(item.status)"
                      >
                        <i class="fas" :class="getStatusIcon(item.status)"></i>
                        {{ getStatusLabel(item.status) }}
                      </span>
                      <button 
                        v-if="item.status === 'rejected' && item.textreject"
                        @click="showRejectReason(item.textreject)"
                        class="mr-2 text-rose-400 hover:text-rose-300 transition text-[10px] underline decoration-dotted"
                      >
                        دلیل رد
                      </button>
                    </td>

                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <button 
                        v-if="item.img" 
                        @click="showImage(item.img)"
                        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-[#b9f1cf] hover:text-[#0a1f14] text-white/70 text-xs transition-all group/btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>مشاهده</span>
                      </button>
                      <span v-else class="text-[10px] text-white/20">---</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- MOBILE LIST -->
          <div class="md:hidden space-y-3">
            <div 
              v-for="item in deposits" 
              :key="item._id"
              class="rounded-2xl bg-[#141a1d] p-4 ring-1 ring-white/5 relative overflow-hidden"
            >
              <!-- Status Stripe -->
              <div class="absolute top-0 right-0 w-1 h-full" :class="getStatusColor(item.status)"></div>

              <div class="flex justify-between items-start mb-3 pl-2">
                <div class="flex items-center gap-2">
                  <span class="text-[10px] text-white/40">کد پیگیری:</span>
                  <span class="font-mono text-sm text-white/90 font-bold">{{ item.uid }}</span>
                </div>
                <div class="flex items-center gap-2">
                    <span 
                        class="px-2 py-0.5 rounded text-[10px] font-bold border"
                        :class="getStatusClass(item.status)"
                    >
                        {{ getStatusLabel(item.status) }}
                    </span>
                </div>
              </div>

              <div v-if="item.status === 'rejected' && item.textreject" class="mb-3 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2 text-[10px] text-rose-300">
                 <span class="font-bold">دلیل رد:</span> {{ item.textreject }}
              </div>

              <div class="flex items-center justify-between mb-3">
                <div>
                  <div class="text-[10px] text-white/50 mb-0.5">مبلغ واریزی</div>
                  <div class="text-xl font-black text-white font-mono tracking-tight">
                    {{ formatMoney(item.amount) }}
                    <span class="text-[10px] font-sans font-normal text-white/40">تومان</span>
                  </div>
                </div>

                <button 
                  v-if="item.img"
                  @click="showImage(item.img)" 
                  class="h-10 w-10 rounded-xl bg-white/5 flex items-center justify-center text-[#b9f1cf] hover:bg-[#b9f1cf] hover:text-[#0a1f14] transition ring-1 ring-white/5"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>

              <div class="pt-3 border-t border-white/5 flex justify-between items-center text-[10px] text-white/30">
                  <span>تاریخ ثبت</span>
                  <span class="font-mono">{{ formatDate(item.createdAt).date }} | {{ formatDate(item.createdAt).time }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'

const API_URL = process.env.VUE_APP_API_URL || ''
const deposits = ref([])
const loading = ref(true)

onMounted(() => {
  fetchDeposits()
})

async function fetchDeposits() {
  loading.value = true
  try {
    const res = await axios.get(`${API_URL}/user/deposit`, { withCredentials: true })
    // دریافت آرایه از data.data یا data
    deposits.value = (res.data.data || res.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  } catch (err) {
    console.error(err)
    // برای نمایش دمو در صورت خطا (در پروداکشن حذف شود)
    // deposits.value = []
  } finally {
    loading.value = false
  }
}

function formatMoney(amount) {
  if (!amount) return '0'
  return Number(amount).toLocaleString('fa-IR')
}

function formatDate(dateString) {
  if (!dateString) return { date: '---', time: '' }
  const d = new Date(dateString)
  return {
    date: d.toLocaleDateString('fa-IR'),
    time: d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })
  }
}

function showImage(url) {
  // اگر آدرس کامل نیست، آدرس پایه را اضافه کن (بسته به ساختار بکند)
  const fullUrl = url.startsWith('http') ? url : `${API_URL}/${url}`
  
  Swal.fire({
    imageUrl: fullUrl,
    imageAlt: 'رسید واریز',
    imageHeight: 400,
    background: '#1a2220',
    color: '#fff',
    showConfirmButton: false,
    showCloseButton: true,
    customClass: {
      popup: 'rounded-2xl border border-[#334D44]',
      image: 'rounded-xl bg-[#0f1417]'
    }
  })
}

function getStatusLabel(status) {
  const map = { 'approved': 'تایید شده', 'rejected': 'رد شده', 'pending': 'در انتظار بررسی' }
  return map[status] || 'در انتظار بررسی'
}

function getStatusIcon(status) {
  const map = { 'approved': 'fa-check-circle', 'rejected': 'fa-times-circle', 'pending': 'fa-clock' }
  return map[status] || 'fa-clock'
}

function getStatusClass(status) {
  if (status === 'approved') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
  if (status === 'rejected') return 'bg-rose-500/10 text-rose-400 border-rose-500/20'
  return 'bg-amber-500/10 text-amber-400 border-amber-500/20'
}

function getStatusColor(status) {
  if (status === 'approved') return 'bg-emerald-500'
  if (status === 'rejected') return 'bg-rose-500'
  return 'bg-amber-500'
}

function showRejectReason(text) {
    Swal.fire({
        title: 'دلیل رد درخواست',
        text: text,
        icon: 'info',
        background: '#1a2220',
        color: '#fff',
        confirmButtonColor: '#b9f1cf',
        confirmButtonText: 'متوجه شدم'
    })
}
</script>

<style scoped>
/* اسکرول بار برای جدول در موبایل */
.overflow-x-auto::-webkit-scrollbar {
  height: 4px;
}
.overflow-x-auto::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}
.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #334D44;
  border-radius: 4px;
}
</style>